import { Component, Vue } from 'vue-property-decorator';
import MiForm, { MiFormField } from '@/components/MiForm/MiForm.ts';
import MiStepDialog from '@/components/MiStepDialog/MiStepDialog.ts';
import { Address } from '@/models/Address';
import { AxiosError } from 'axios';
import { Contractor, ContractorCreatePayload } from '@/models/Contractor';
import { Rpc } from '@/models/Rpc';
import ErrorHandler from '@/support/ErrorHandler';

@Component<CreateContractorDialog>({ })
export default class CreateContractorDialog extends Vue {
  protected isLoading = false;

  protected isSuccessful = false;

  protected isFailed = false;

  protected payload: ContractorCreatePayload = {};

  protected currentStep = 1;

  protected activeTab: number | null = 1;

  protected isFindingAddress = false;

  protected addressError: string | null = null;

  protected address: Address | null = null;

  protected autoFillAddress = true;

  protected passwordErrorMessage = '';

  protected onStepChanged(step: number): void {
    this.currentStep = step;
    this.validateCurrentForm();
  }

  protected async validateCurrentForm(): Promise<void> {
    switch (this.currentStep) {
      default:
      case 1: this.setFormValid(this.isCompanyFormValid()); break;
      case 2: this.setFormValid(this.isAddressFormValid()); break;
      case 3: this.setFormValid(this.isContactFormValid()); break;
      case 4: this.setFormValid(await this.isPasswordFormValid()); break;
    }
  }

  protected close(): void {
    this.$emit('input', false);
  }

  protected async toggleAutoFill(): Promise<void> {
    const autoFillFields = ['address.street', 'address.city'];
    if (this.autoFillAddress) {
      this.autoFillAddress = false;
      this.addressForm.disableFields(autoFillFields, false);
      return;
    }

    this.addressForm.disableFields(autoFillFields, true);
    this.autoFillAddress = true;
    this.fetchAddress();
  }

  protected setAddress(address: Record<string, string>): void {
    this.addressForm.setValue('address.street', address.street);
    this.addressForm.setValue('address.city', address.city);
  }

  protected setAddressError(message: string): void {
    let errorMessage = 'Dit adres is niet gevonden, controleer de adresgegevens.';
    if (message === 'addition not found') {
      errorMessage = 'Deze toevoeging is niet gevonden, controleer de adresgegevens';
    } else if (message === 'Streetnumber not found') {
      errorMessage = 'Dit huisnummer is niet gevonden, controleer de adresgegevens';
    } else if (message === 'post code not found') {
      errorMessage = 'Deze postcode is niet gevonden, controleer de adresgegevens';
    }

    this.addressError = errorMessage;
  }

  protected async onClickCreate(): Promise<Contractor | void> {
    this.isLoading = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = this.payload;

    const response: Contractor | null = await new Contractor()
      .create(payload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        this.isFailed = true;
        return null;
      });

    this.isLoading = false;

    if (! response) {
      this.isFailed = true;
      return;
    }

    this.isSuccessful = true;
    this.$emit('isCreated');
    setTimeout(() => {
      this.close();
    }, 2000);
  }

  protected async handleFieldChanged(field: MiFormField): Promise<void> {
    this.payload = await this.createPayload();
    if (this.currentStep === 2 && this.autoFillAddress && field.key !== 'address.city' && field.key !== 'address.street') {
      this.fetchAddress();
    }

    this.validateCurrentForm();
  }

  protected async fetchAddress(): Promise<void> {
    const data = await this.addressForm.parseFromData();
    this.isFindingAddress = true;
    this.setAddress({ street: '', city: '' });

    if (! data || ! data.address.postcode || ! data.address.number) { return; }
    const addressResponse = await this.rpcPost('address:suggest', {
      postcode: data.address.postcode ? data.address.postcode : '',
      number: data.address.number ? data.address.number : '',
      addition: data.address.number_add && data.address.number_add.length ? data.address.number_add : '',
    });

    this.isFindingAddress = false;

    if (addressResponse) {
      this.addressError = null;
      this.setAddress(addressResponse);
    }
  }

  protected async rpcPost(signature: string, body: Record<string, string | string[]>): Promise<any> {
    const payload = {
      signature,
      body,
    };

    const rpcResponse = await new Rpc()
      .post(payload);

    return rpcResponse;
  }

  protected isCompanyFormValid(): boolean {
    if (! this.companyForm) { return false; }
    const isValid: boolean = this.companyForm.isFormValid();
    if (! isValid) { return false; }
    return true;
  }

  protected isAddressFormValid(): boolean {
    if (! this.addressForm) { return false; }
    const isValid: boolean = this.addressForm.isFormValid();
    if (! isValid) { return false; }
    return true;
  }

  protected isContactFormValid(): boolean {
    if (! this.contactForm) { return false; }
    const isValid: boolean = this.contactForm.isFormValid();
    if (! isValid) { return false; }
    return true;
  }

  protected async isPasswordFormValid(): Promise<boolean> {
    if (! this.passwordForm) { return false; }
    const data = await this.passwordForm.parseFromData();

    if (data.password && data.confirm_password && (data.password !== data.confirm_password)) {
      this.passwordErrorMessage = 'Wachtwoorden komen niet overeen';
      return false;
    }

    this.passwordErrorMessage = '';
    return data.password === data.confirm_password;
  }

  protected setFormValid(valid: boolean): void {
    this.stepDialog.setValid(valid);
  }

  protected async createPayload(): Promise<ContractorCreatePayload> {
    const passwordForm = await this.passwordForm.parseFromData();
    const payload = {
      ...await this.companyForm.parseFromData(),
      ...await this.addressForm.parseFromData(),
      ...await this.contactForm.parseFromData(),
      password: passwordForm.password,
    };
    return payload;
  }

  protected get companyForm(): MiForm {
    return this.$refs.companyForm as MiForm;
  }

  protected get addressForm(): MiForm {
    return this.$refs.addressForm as MiForm;
  }

  protected get contactForm(): MiForm {
    return this.$refs.contactForm as MiForm;
  }

  protected get passwordForm(): MiForm {
    return this.$refs.passwordForm as MiForm;
  }

  protected get stepDialog(): MiStepDialog {
    return this.$refs.stepDialog as MiStepDialog;
  }

  protected get companyFields(): MiFormField[] {
    return [
      {
        type: 'text',
        key: 'name',
        placeholder: 'Name',
        label: 'Name',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'coc',
        placeholder: 'KVK-nummer',
        label: 'KVK-nummer',
        rules: ['required'],
      },
      {
        type: 'date',
        key: 'registered_since',
        placeholder: 'Ingeschreven bij de KVK sinds',
        label: 'Ingeschreven bij de KVK sinds',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'vat_number',
        placeholder: 'BTW-nummer',
        label: 'BTW-nummer',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'purchase_order_number',
        placeholder: 'Inkoopordernummer',
        label: 'Inkoopordernummer',
        rules: ['required'],
      },
    ];
  }

  protected get addressFields(): MiFormField[] {
    return [
      {
        type: 'text',
        key: 'address.postcode',
        placeholder: 'Postcode',
        label: 'Postcode',
        rules: ['required', 'dutchPostcode'],
        class: 'w-1/2',
      },
      {
        type: 'number',
        key: 'address.number',
        placeholder: 'Huisnummer',
        label: 'Huisnummer',
        rules: ['required'],
        class: 'w-1/4 pl-32',
      },
      {
        type: 'text',
        key: 'address.number_add',
        placeholder: 'Toevoeging',
        label: 'Toevoeging',
        class: 'w-1/4 pl-32',
      },
      {
        type: 'text',
        key: 'address.street',
        placeholder: 'Straat',
        value: this.address && this.address.street ? this.address.street : '',
        label: 'Straat',
        rules: ['required'],
        disabled: true,
        hideDetails: true,
      },
      {
        type: 'text',
        key: 'address.city',
        placeholder: 'Stad',
        label: 'Stad',
        rules: ['required'],
        disabled: true,
        hideDetails: true,
      },
    ];
  }

  protected get contactFields(): MiFormField[] {
    return [
      {
        type: 'text',
        key: 'contact_name',
        placeholder: 'Contactpersoon',
        label: 'Contactpersoon',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'contact_function',
        placeholder: 'Functie contactpersoon',
        label: 'Functie contactpersoon',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'site',
        placeholder: 'Website',
        label: 'Website',
      },
      {
        type: 'text',
        key: 'contact_email',
        placeholder: 'Email',
        label: 'Email',
        rules: ['required', 'email'],
      },
      {
        type: 'text',
        key: 'phone',
        placeholder: 'Telefoonnummer',
        label: 'Telefoonnummer',
        rules: ['required', 'dutchPhone'],
      },
    ];
  }

  protected get passwordFields(): MiFormField[] {
    return [
      {
        type: 'password',
        key: 'password',
        placeholder: 'Wachtwoord',
        label: 'Wachtwoord',
        rules: ['required', 'password'],
      },
      {
        type: 'password',
        key: 'confirm_password',
        placeholder: 'Wachtwoord bevestigen',
        label: 'Wachtwoord bevestigen',
        rules: ['required'],
      },
    ];
  }
}
